import axios from 'axios';
import { API_CONFIG } from '../config/env';

export interface GlobalStats {
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
}

export interface UserStats {
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
}

export interface SubUser {
  userId: number;
  stats: UserStats;
}

export interface UserStatsResponse {
  userId: number;
  totalIssued: number;
  totalCompleted: number;
  totalPending: number;
  totalFailed: number;
  totalVolume: number;
  subUsers: SubUser[];
}

export interface OpsAlertBucket {
  count: number;
  thresholdMinutes?: number | null;
  oldestAgeMinutes?: number | null;
  oldestCreatedAtUtc?: string | null;
  oldestTransactionId?: number | null;
  oldestReference?: string | null;
  oldestMerchantUserId?: string | null;
  oldestCaseOwnerName?: string | null;
}

export interface OpsAlertSummary {
  generatedAtUtc: string;
  openExceptionsOlderThanThreshold: OpsAlertBucket;
  webhookRetriesOlderThanThreshold: OpsAlertBucket;
  unresolvedUnderpayments: OpsAlertBucket;
  overdueCases: OpsAlertBucket;
  escalatedCases: OpsAlertBucket;
  myAssignedCases: OpsAlertBucket;
  unassignedCases: OpsAlertBucket;
  reminderDueCases: OpsAlertBucket;
  escalationDueCases: OpsAlertBucket;
}

export interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  settlementWallet: string;
  usdtWallet: string;
  role: 'Admin' | 'SuperAgent' | 'Agent';
  createdAt?: string;
  registrationDate?: string;
  createdById?: number;
}

export interface Transaction {
  id: number;
  userId: number;
  userName: string;
  amount: number;
  currency: string;
  status: 'Issued' | 'Completed' | 'Pending' | 'Failed' | 'PayoutCompleted' | 'Expired';
  displayStatus?: string;
  timestamp: string;
  createdAt?: string;
  updatedAt?: string;
  paymentMethod?: string;
  fiatAmount?: number;
  fiatCurrency?: string;
}

const mapUser = (user: any): User => {
  const settlementWallet = user.usdcWallet || user.usdtWallet || '';

  return {
    ...user,
    settlementWallet,
    usdtWallet: settlementWallet
  };
};

class DashboardService {
  private getAuthHeaders() {
    const token = localStorage.getItem('accessToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async getGlobalStats(): Promise<GlobalStats> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/Stats/global`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching global stats:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response status:', error.response?.status);
        console.error('Response data:', error.response?.data);
      }
      throw error;
    }
  }

  async getUserStats(userId: number): Promise<UserStatsResponse> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/Stats/user/${userId}`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {

      throw error;
    }
  }

  async getUsers(): Promise<User[]> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/Users`, {
        headers: this.getAuthHeaders(),
      });
      const users = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.value)
          ? response.data.value
          : Array.isArray(response.data?.items)
            ? response.data.items
            : [];
      return users.map(mapUser);
    } catch (error) {

      throw error;
    }
  }

  async getOpsSummary(openExceptionOlderThanMinutes: number = 30, webhookRetryOlderThanMinutes: number = 15): Promise<OpsAlertSummary> {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/Stats/ops-summary`, {
      headers: this.getAuthHeaders(),
      params: {
        openExceptionOlderThanMinutes,
        webhookRetryOlderThanMinutes,
      },
    });

    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/Users/me`, {
        headers: this.getAuthHeaders(),
      });

      return mapUser(response.data);
    } catch (error) {
      throw error;
    }
  }

  async updateUserWallet(userId: number, newWalletAddress: string): Promise<void> {
    try {
      await axios.put(
        `${API_CONFIG.BASE_URL}/Users/${userId}/address`,
        { newWalletAddress },
        {
          headers: this.getAuthHeaders(),
        }
      );
    } catch (error) {
      throw error;
    }
  }

  async getTransactions(limit: number = 5): Promise<Transaction[]> {
    try {
      const response = await axios.get(`${API_CONFIG.BASE_URL}/Transactions`, {
        headers: this.getAuthHeaders(),
        params: {
          page: 1,
          pageSize: limit,
        },
      });
      if (Array.isArray(response.data)) {
        return response.data;
      }

      if (Array.isArray(response.data?.items)) {
        return response.data.items;
      }

      return [];
    } catch (error) {

      throw error;
    }
  }
}

export const dashboardService = new DashboardService();
